import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Loader2,
  Phone as PhoneIcon,
  Mail,
  Cake,
  Calendar,
  Save,
  Check,
  Merge,
  Minus,
  Pencil,
  Gift,
  Plus,
  X,
} from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import apiClient from '@/api/client'
import { useToast } from '@/hooks/use-toast'
import { formatCurrency, formatDateTimeHuman, formatRelative } from '@/lib/utils'
import { MAX_CUSTOMER_PHONE_LEN } from '@/lib/customerPhone'
import {
  DAY_OPTIONS,
  MONTH_OPTIONS,
  formatMonthDay,
  splitMonthDay,
  toMonthDayISO,
} from '@/lib/birthday'
import type {
  APIResponse,
  CustomerDetail,
  CustomerLoyaltyHistory,
  CustomerOrderHistoryItem,
  CustomerUpdateConflict,
  LifecycleStage,
  UpdateCustomerRequest,
} from '@/types'
import { STAGE_BADGE, STAGE_LABEL } from './AdminCustomersDirectory'
import { AdminCustomerAddresses } from './AdminCustomerAddresses'
import { AdminCustomerMergeDialog } from './AdminCustomerMergeDialog'

interface AdminCustomerProfileProps {
  customerID: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Profile dialog opened from a row in AdminCustomersDirectory.
 *
 * Layout: header (name + lifecycle badge), 4 stat tiles (lifetime spend,
 * visit count, avg ticket, last visit), contact strip (phone + email +
 * birthday), notes textarea (saves on blur), and an order-history table
 * underneath. Notes are capped at 4000 chars by the server.
 */
export function AdminCustomerProfile({
  customerID,
  open,
  onOpenChange,
}: AdminCustomerProfileProps) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const detailQuery = useQuery<APIResponse<CustomerDetail>>({
    queryKey: ['customer', customerID],
    queryFn: () => apiClient.getCustomer(customerID as string),
    enabled: Boolean(customerID) && open,
    staleTime: 15_000,
  })

  const ordersQuery = useQuery<APIResponse<CustomerOrderHistoryItem[]>>({
    queryKey: ['customer', customerID, 'orders'],
    queryFn: () => apiClient.getCustomerOrderHistory(customerID as string, 50),
    enabled: Boolean(customerID) && open,
    staleTime: 30_000,
  })

  const loyaltyQuery = useQuery<APIResponse<CustomerLoyaltyHistory>>({
    queryKey: ['customer', customerID, 'loyalty-history'],
    queryFn: () => apiClient.getCustomerLoyaltyHistory(customerID as string),
    enabled: Boolean(customerID) && open,
    staleTime: 30_000,
  })

  const detail = detailQuery.data?.data
  const orders = ordersQuery.data?.data ?? []
  const loyalty = loyaltyQuery.data?.data
  const loyaltyEvents = loyalty?.events ?? []

  const [notes, setNotes] = useState<string>('')
  const [savedAt, setSavedAt] = useState<number | null>(null)
  const [mergeOpen, setMergeOpen] = useState(false)
  const [adjustOpen, setAdjustOpen] = useState(false)

  // Hydrate the textarea whenever a new customer's detail arrives.
  useEffect(() => {
    if (detail) setNotes(detail.notes ?? '')
  }, [detail])

  const notesMutation = useMutation({
    mutationFn: (value: string) =>
      apiClient.updateCustomerNotes(customerID as string, value),
    onSuccess: () => {
      setSavedAt(Date.now())
      queryClient.invalidateQueries({ queryKey: ['customer', customerID] })
      // The list page shows lifetime spend + visit count + stage — none
      // of which notes affect — so we don't refetch ['customers','list'].
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : String(err)
      toast({ title: 'Notes not saved', description: msg, variant: 'destructive' })
    },
  })

  // Single mutation shared by every editable field. Each call sends one
  // key from UpdateCustomerRequest. Centralising it keeps cache
  // invalidation in one place and makes the conflict handling uniform.
  const fieldMutation = useMutation<
    APIResponse<CustomerDetail | CustomerUpdateConflict>,
    Error,
    { patch: UpdateCustomerRequest; label: string }
  >({
    mutationFn: ({ patch }) =>
      apiClient.updateCustomer(customerID as string, patch),
    onSuccess: (res, vars) => {
      // 4xx responses (including 409) come through as success === false
      // because the axios layer doesn't throw on 2xx-only — handle them
      // here as if they were errors.
      if (!res.success) {
        const code = res.error
        if (code === 'phone_conflict' || code === 'email_conflict') {
          const conflict = res.data as CustomerUpdateConflict | undefined
          const name = conflict?.conflicting_customer_name || 'another customer'
          const field = code === 'phone_conflict' ? 'phone' : 'email'
          toast({
            title: `Couldn't save ${field}`,
            description: `This ${field} is already on ${name}'s profile. Open it from the directory to merge.`,
            variant: 'destructive',
          })
          return
        }
        toast({
          title: `${vars.label} not saved`,
          description: res.message || 'Save failed',
          variant: 'destructive',
        })
        return
      }
      // Happy path: replace the cached detail with the fresh row the
      // server just returned so the modal refreshes instantly, and
      // invalidate the directory list since name/phone/email/birthday
      // are all visible there.
      const fresh = res.data as CustomerDetail
      queryClient.setQueryData<APIResponse<CustomerDetail>>(
        ['customer', customerID],
        (prev) => ({
          success: true,
          message: prev?.message ?? 'ok',
          data: fresh,
        }),
      )
      queryClient.invalidateQueries({ queryKey: ['customers', 'list'] })
      setSavedAt(Date.now())
    },
    onError: (err, vars) => {
      const msg = err instanceof Error ? err.message : String(err)
      toast({ title: `${vars.label} not saved`, description: msg, variant: 'destructive' })
    },
  })

  const isDirty = detail ? (notes ?? '') !== (detail.notes ?? '') : false
  const saving = notesMutation.isPending

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showCloseButton={false} className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col p-0">
        {!detail ? (
          <div className="flex h-64 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <DialogHeader className="space-y-2 border-b border-border/70 bg-gradient-to-r from-muted/50 via-muted/30 to-transparent px-6 py-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 space-y-1">
                  <DialogTitle asChild>
                    <div className="flex items-center gap-3 text-xl">
                      <EditableName
                        value={detail.display_name ?? ''}
                        onSave={(v) =>
                          fieldMutation.mutateAsync({
                            patch: { display_name: v },
                            label: 'Name',
                          })
                        }
                        saving={
                          fieldMutation.isPending &&
                          fieldMutation.variables?.patch.display_name !== undefined
                        }
                      />
                      {detail.lifecycle_stage && (
                        <Badge
                          variant="outline"
                          className={`px-2 py-0.5 text-[10px] uppercase tracking-wider ${STAGE_BADGE[detail.lifecycle_stage as LifecycleStage]}`}
                        >
                          {STAGE_LABEL[detail.lifecycle_stage as LifecycleStage]}
                        </Badge>
                      )}
                    </div>
                  </DialogTitle>
                  <DialogDescription className="text-xs">
                    First visit{' '}
                    {detail.first_visit_at ? formatRelative(detail.first_visit_at) : '—'}
                    {' · '}
                    Last visit{' '}
                    {detail.last_visit_at ? formatRelative(detail.last_visit_at) : '—'}
                  </DialogDescription>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setMergeOpen(true)}
                  className="h-8 gap-1.5 text-xs shrink-0"
                >
                  <Merge className="h-3.5 w-3.5" />
                  Find duplicates
                </Button>
              </div>
            </DialogHeader>

            <div className="space-y-6 overflow-y-auto px-6 py-5">
              {/* Stat tiles */}
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <StatTile label="Lifetime spend" value={formatCurrency(detail.lifetime_spend)} />
                <StatTile label="Visits" value={detail.visit_count.toLocaleString()} />
                <StatTile
                  label="Avg ticket"
                  value={detail.avg_ticket != null ? formatCurrency(detail.avg_ticket) : '—'}
                />
                <StatTile
                  label="Last visit"
                  value={detail.last_visit_at ? formatRelative(detail.last_visit_at) : 'Never'}
                />
              </div>

              {/* Contact strip */}
              <div className="grid gap-2 sm:grid-cols-3">
                <EditableContactCell
                  icon={PhoneIcon}
                  label="Phone"
                  value={detail.phone_normalized || detail.phone || ''}
                  rawValue={detail.phone ?? detail.phone_normalized ?? ''}
                  href={detail.phone_normalized ? `tel:${detail.phone_normalized}` : undefined}
                  inputType="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="0301 1234567"
                  hint="Pakistani numbers are stored as +92…"
                  onSave={(v) =>
                    fieldMutation.mutateAsync({
                      patch: { phone: v },
                      label: 'Phone',
                    })
                  }
                  saving={
                    fieldMutation.isPending &&
                    fieldMutation.variables?.patch.phone !== undefined
                  }
                />
                <EditableContactCell
                  icon={Mail}
                  label="Email"
                  value={detail.email ?? ''}
                  rawValue={detail.email ?? ''}
                  href={detail.email ? `mailto:${detail.email}` : undefined}
                  inputType="email"
                  autoComplete="email"
                  placeholder="name@example.com"
                  onSave={(v) =>
                    fieldMutation.mutateAsync({
                      patch: { email: v },
                      label: 'Email',
                    })
                  }
                  saving={
                    fieldMutation.isPending &&
                    fieldMutation.variables?.patch.email !== undefined
                  }
                />
                <EditableBirthdayCell
                  value={detail.birthday ?? ''}
                  onSave={(v) =>
                    fieldMutation.mutateAsync({
                      patch: { birthday: v },
                      label: 'Birthday',
                    })
                  }
                  saving={
                    fieldMutation.isPending &&
                    fieldMutation.variables?.patch.birthday !== undefined
                  }
                />
              </div>

              {/* Address book (F-FE-N30) — same rows the storefront account
                  and counter prefill use. */}
              <AdminCustomerAddresses customerID={detail.id} />

              {/* Notes */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Notes
                  </label>
                  <div className="flex items-center gap-2">
                    {savedAt && !isDirty && Date.now() - savedAt < 4000 && (
                      <span className="flex items-center gap-1 text-xs text-emerald-600">
                        <Check className="h-3 w-3" /> Saved
                      </span>
                    )}
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={!isDirty || saving}
                      onClick={() => notesMutation.mutate(notes)}
                      className="h-7 gap-1.5 text-xs"
                    >
                      {saving ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Save className="h-3 w-3" />
                      )}
                      Save
                    </Button>
                  </div>
                </div>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Allergies, favourite table, regular order, anything the team should know."
                  rows={3}
                  maxLength={4000}
                  className="resize-none"
                />
              </div>

              {/* Loyalty — only shown when the programme is on. Surfaces the
                  live balance + a merged earn/redeem timeline so support can
                  answer "did they already use their reward?". */}
              {loyalty?.enabled && (
                <div className="space-y-2">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Loyalty
                  </h3>
                  <div className="space-y-3 rounded-lg border border-border/80 p-3">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                      <Gift className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium tabular-nums">{loyalty.balance}</span>
                      <span className="text-muted-foreground">
                        / {loyalty.threshold} visits toward {loyalty.reward_label}
                      </span>
                      {loyalty.balance >= loyalty.threshold && (
                        <Badge className="ml-1 bg-emerald-600 text-[10px] uppercase tracking-wider text-white hover:bg-emerald-600">
                          Reward ready
                        </Badge>
                      )}
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setAdjustOpen(true)}
                        className="ml-auto h-7 gap-1.5 text-xs"
                      >
                        <Plus className="h-3 w-3" />
                        Adjust punches
                      </Button>
                    </div>
                    {loyaltyQuery.isLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    ) : loyaltyEvents.length === 0 ? (
                      <p className="text-xs text-muted-foreground">No loyalty activity yet.</p>
                    ) : (
                      <div className="overflow-hidden rounded-md border border-border/70">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-muted/40">
                              <TableHead className="w-[30%]">When</TableHead>
                              <TableHead>Event</TableHead>
                              <TableHead className="w-[16%]">Order #</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {loyaltyEvents.map((ev, i) => (
                              <TableRow
                                key={`${ev.type}-${ev.order_id}-${i}`}
                                className={ev.voided ? 'opacity-50' : ''}
                              >
                                <TableCell className="text-xs">
                                  <div className="flex items-center gap-1.5">
                                    <Calendar className="h-3 w-3 text-muted-foreground" />
                                    {formatDateTimeHuman(ev.at)}
                                  </div>
                                </TableCell>
                                <TableCell className="text-xs">
                                  {ev.type === 'earned' ? (
                                    <span
                                      className={
                                        ev.voided
                                          ? 'text-muted-foreground line-through'
                                          : 'text-emerald-700 dark:text-emerald-400'
                                      }
                                    >
                                      {/* A single earn event can be worth several
                                          stamps under the per-item earn basis, so
                                          show the count rather than letting a
                                          5-stamp order look like a 1-stamp one and
                                          leaving the balance unexplained. Rows
                                          written before per-item earning have no
                                          punch_count — treat those as 1. */}
                                      {ev.source === 'manual'
                                        ? `Manual: +${ev.punch_count ?? 1}${ev.note ? ` (${ev.note})` : ''}`
                                        : (ev.punch_count ?? 1) > 1
                                        ? `+${ev.punch_count} stamps earned`
                                        : 'Visit earned'}
                                      {ev.voided ? ' (reversed)' : ''}
                                      {/* Attribution — the cashier who took the
                                          payment or the manager behind a grant.
                                          Absent on guest-initiated punches. */}
                                      {ev.created_by_name && (
                                        <span className="text-muted-foreground">
                                          {' '}· by {ev.created_by_name}
                                        </span>
                                      )}
                                    </span>
                                  ) : (
                                    <span>
                                      Reward redeemed
                                      <span className="text-muted-foreground">
                                        {' '}· −{ev.punches_spent} visits ·{' '}
                                        {formatCurrency(ev.discount_applied ?? 0)} off
                                      </span>
                                    </span>
                                  )}
                                </TableCell>
                                <TableCell className="font-mono text-xs">
                                  {ev.order_number ?? '—'}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Order history */}
              <div className="space-y-2">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Order history
                </h3>
                <div className="rounded-lg border border-border/80 overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/40">
                        <TableHead className="w-[12%]">Order #</TableHead>
                        <TableHead className="w-[22%]">When</TableHead>
                        <TableHead className="w-[14%]">Type</TableHead>
                        <TableHead className="w-[14%]">Status</TableHead>
                        <TableHead className="w-[10%] text-right">Items</TableHead>
                        <TableHead className="w-[14%] text-right">Discount</TableHead>
                        <TableHead className="w-[14%] text-right">Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {ordersQuery.isLoading ? (
                        <TableRow>
                          <TableCell colSpan={7} className="h-24 text-center">
                            <Loader2 className="mx-auto h-4 w-4 animate-spin text-muted-foreground" />
                          </TableCell>
                        </TableRow>
                      ) : orders.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} className="h-24 text-center text-xs text-muted-foreground">
                            No orders linked to this customer yet.
                          </TableCell>
                        </TableRow>
                      ) : (
                        orders.map((o) => (
                          <TableRow
                            key={o.id}
                            className={o.cancelled_by_credit_note ? 'opacity-60' : ''}
                          >
                            <TableCell className="font-mono text-xs">{o.order_number}</TableCell>
                            <TableCell className="text-xs">
                              <div className="flex items-center gap-1.5">
                                <Calendar className="h-3 w-3 text-muted-foreground" />
                                {formatDateTimeHuman(o.completed_at || o.created_at)}
                              </div>
                            </TableCell>
                            <TableCell className="text-xs capitalize">
                              {o.order_type.replace(/_/g, ' ')}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-[10px] uppercase tracking-wider">
                                {o.cancelled_by_credit_note ? 'Credited' : o.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right tabular-nums text-xs">
                              {o.item_count}
                            </TableCell>
                            <TableCell className="text-right tabular-nums text-xs">
                              {o.discount_amount > 0 ? `−${formatCurrency(o.discount_amount)}` : '—'}
                            </TableCell>
                            <TableCell className="text-right tabular-nums text-xs font-medium">
                              {formatCurrency(o.total_amount)}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
            <AdminCustomerMergeDialog
              sourceID={customerID}
              sourceName={detail.display_name}
              open={mergeOpen}
              onOpenChange={setMergeOpen}
              onMerged={() => {
                // Close the profile after a merge — the row no longer
                // exists from the directory's POV.
                onOpenChange(false)
              }}
            />
            {loyalty?.enabled && customerID && (
              <AdjustPunchesDialog
                customerID={customerID}
                customerName={detail.display_name ?? ''}
                open={adjustOpen}
                onOpenChange={setAdjustOpen}
              />
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

/** How many punches one manual grant can hand out. Mirrors the server cap
 *  (maxManualPunchGrant in marketing_loyalty_adjust.go) — change together. */
const MAX_MANUAL_PUNCHES = 5

/**
 * Manual punch grant ("give my friend +3"). PIN-gated like credit notes: the
 * grant is attributed to the admin/manager whose PIN authorises it, and the
 * required reason lands in the ledger — the audit trail is the control.
 * Positive adjustments only; refunds reverse punches through the existing
 * void paths.
 */
function AdjustPunchesDialog({
  customerID,
  customerName,
  open,
  onOpenChange,
}: {
  customerID: string
  customerName: string
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [punches, setPunches] = useState(1)
  const [reason, setReason] = useState('')
  const [pin, setPin] = useState('')

  // Fresh form every open — a cancelled grant must not leak into the next one.
  useEffect(() => {
    if (open) {
      setPunches(1)
      setReason('')
      setPin('')
    }
  }, [open])

  const mutation = useMutation({
    mutationFn: () =>
      apiClient.adjustCustomerLoyalty(customerID, {
        punches,
        reason: reason.trim(),
        pin: pin.trim(),
      }),
    onSuccess: (res) => {
      // 4xx comes through as success === false (axios layer doesn't throw).
      if (!res.success) {
        toast({
          title: 'Punches not added',
          description:
            res.error === 'invalid_pin'
              ? 'That PIN does not match any active admin or manager.'
              : res.message || 'Adjustment failed',
          variant: 'destructive',
        })
        return
      }
      toast({
        title: 'Punches added',
        description: `+${res.data?.granted} — balance is now ${res.data?.balance}/${res.data?.threshold}.`,
      })
      queryClient.invalidateQueries({ queryKey: ['customer', customerID, 'loyalty-history'] })
      onOpenChange(false)
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : String(err)
      toast({ title: 'Punches not added', description: msg, variant: 'destructive' })
    },
  })

  const pinIsValid = /^\d{4}$/.test(pin.trim())
  const canSubmit = reason.trim().length > 0 && pinIsValid && !mutation.isPending

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Adjust punches</DialogTitle>
          <DialogDescription>
            Add visits to {customerName || 'this customer'}&apos;s loyalty card. The grant is
            recorded in the ledger with your name and reason.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Punches to add
            </label>
            <div className="flex items-center justify-center gap-3">
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-9 w-9 px-0"
                onClick={() => setPunches((p) => Math.max(1, p - 1))}
                disabled={punches <= 1 || mutation.isPending}
                aria-label="Fewer punches"
              >
                <Minus className="h-4 w-4" />
              </Button>
              <span className="w-12 text-center text-2xl font-semibold tabular-nums">
                +{punches}
              </span>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-9 w-9 px-0"
                onClick={() => setPunches((p) => Math.min(MAX_MANUAL_PUNCHES, p + 1))}
                disabled={punches >= MAX_MANUAL_PUNCHES || mutation.isPending}
                aria-label="More punches"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="space-y-2">
            <label
              htmlFor="adjust-reason"
              className="text-xs font-medium uppercase tracking-wider text-muted-foreground"
            >
              Reason (required)
            </label>
            <Input
              id="adjust-reason"
              placeholder="e.g. Goodwill — missed stamp on last visit"
              value={reason}
              maxLength={200}
              onChange={(e) => setReason(e.target.value)}
              disabled={mutation.isPending}
            />
          </div>
          <div className="space-y-2">
            <label
              htmlFor="adjust-pin"
              className="text-xs font-medium uppercase tracking-wider text-muted-foreground"
            >
              Admin / Manager PIN (4 digits)
            </label>
            <Input
              id="adjust-pin"
              type="password"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={4}
              placeholder="••••"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
              className="font-mono text-center tracking-[0.5em]"
              disabled={mutation.isPending}
            />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button type="button" size="sm" onClick={() => mutation.mutate()} disabled={!canSubmit}>
              {mutation.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
              Add {punches} {punches === 1 ? 'punch' : 'punches'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function StatTile({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded-md border border-border/70 bg-background/80 px-3 py-2 shadow-sm">
      <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className="mt-1 text-base font-semibold tabular-nums text-foreground">{value}</div>
    </div>
  )
}

// EditableContactCell is the inline-edit version of the old read-only
// ContactCell. Display mode shows the value (and a pencil button on
// hover); edit mode swaps in an <Input> with Cancel / Save controls
// that mirror the Notes section styling.
//
// `value` is what to render in display mode (e.g. normalised phone);
// `rawValue` is what populates the input in edit mode (the raw user
// input — for phone, that's the un-normalised string the cashier
// originally typed).
/**
 * Birthday, edited as a month and a day (F-FE-N32).
 *
 * Its own component rather than a mode on EditableContactCell: two controls, a
 * validity rule spanning both, and a clear action are different enough that
 * folding them in would make the shared cell answer to two masters.
 *
 * Deliberately not `<input type="date">`, which this cell used to be. A date
 * picker structurally demands a year, and the join form has decided not to ask
 * for one — so most birthdays now carry a placeholder year that a date picker
 * would display as fact.
 *
 * The year is never shown here even for counter-captured birthdays that have a
 * real one. Showing it for those and not the others would advertise exactly the
 * distinction the design keeps out of the interface.
 */
function EditableBirthdayCell({
  value,
  onSave,
  saving,
}: {
  /** Stored ISO date, or '' for none. May carry a real year or the placeholder. */
  value: string
  /** Receives an ISO date to set, or '' to clear. */
  onSave: (value: string) => Promise<unknown>
  saving: boolean
}) {
  const [editing, setEditing] = useState(false)
  const [month, setMonth] = useState<number | ''>('')
  const [day, setDay] = useState<number | ''>('')

  // Re-hydrate on every open so a cancelled edit never leaks into the next one.
  useEffect(() => {
    if (editing) {
      const s = splitMonthDay(value)
      setMonth(s.month)
      setDay(s.day)
    }
  }, [editing, value])

  const next = toMonthDayISO(month, day)
  const cleared = month === '' && day === ''
  // Half-filled is neither a valid birthday nor a clear, so there is nothing
  // honest to save — the Save button stays disabled and says why.
  const halfFilled = !cleared && next == null
  const dirty = cleared ? value !== '' : next != null && next !== value

  async function handleSave() {
    if (saving || halfFilled || !dirty) return
    await onSave(cleared ? '' : (next as string))
    setEditing(false)
  }

  const selectClass =
    'h-8 w-full rounded-md border border-input bg-background px-2 text-sm ' +
    'focus:outline-none focus:ring-1 focus:ring-ring'

  return (
    <div className="group rounded-md border border-border/70 bg-background/80 px-3 py-2.5 shadow-sm">
      <div className="flex items-center justify-between gap-2 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <Cake className="h-3 w-3" aria-hidden /> Birthday
        </span>
        {!editing && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100 focus:outline-none text-muted-foreground hover:text-foreground"
            aria-label="Edit birthday"
          >
            <Pencil className="h-3 w-3" />
          </button>
        )}
      </div>
      {editing ? (
        <div className="mt-1.5 space-y-1.5">
          <div className="grid grid-cols-[1.4fr_1fr] gap-1.5">
            <select
              className={selectClass}
              aria-label="Birthday month"
              value={month}
              onChange={(e) => setMonth(e.target.value === '' ? '' : Number(e.target.value))}
            >
              <option value="">Month</option>
              {MONTH_OPTIONS.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
            <select
              className={selectClass}
              aria-label="Birthday day"
              value={day}
              onChange={(e) => setDay(e.target.value === '' ? '' : Number(e.target.value))}
            >
              <option value="">Day</option>
              {DAY_OPTIONS.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>
          <p className="text-[10px] text-muted-foreground">
            {halfFilled
              ? month !== '' && day !== ''
                ? 'That day does not exist in that month.'
                : 'Pick both a month and a day, or clear both.'
              : 'Day and month only — the year is not stored.'}
          </p>
          <div className="flex justify-end gap-1.5">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setEditing(false)}
              className="h-7 gap-1 text-xs"
              disabled={saving}
            >
              <X className="h-3 w-3" />
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={handleSave}
              disabled={!dirty || halfFilled || saving}
              className="h-7 gap-1.5 text-xs"
            >
              {saving ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Save className="h-3 w-3" />
              )}
              Save
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-1 truncate text-sm font-medium text-foreground">
          {formatMonthDay(value) || (
            <span className="font-normal text-muted-foreground">—</span>
          )}
        </div>
      )}
    </div>
  )
}

function EditableContactCell({
  icon: Icon,
  label,
  value,
  rawValue,
  href,
  inputType = 'text',
  inputMode,
  autoComplete,
  placeholder,
  hint,
  onSave,
  saving,
}: {
  icon: typeof PhoneIcon
  label: string
  value: string
  rawValue: string
  href?: string
  inputType?: 'text' | 'email' | 'tel' | 'date'
  inputMode?: 'text' | 'tel' | 'email'
  autoComplete?: string
  placeholder?: string
  hint?: string
  onSave: (value: string) => Promise<unknown>
  saving: boolean
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(rawValue)
  const inputRef = useRef<HTMLInputElement | null>(null)

  // Re-hydrate the draft every time we enter edit mode so we never show
  // stale input from a previous open / close.
  useEffect(() => {
    if (editing) {
      setDraft(rawValue)
      // Focus on the next tick so the input has mounted.
      const t = setTimeout(() => inputRef.current?.focus(), 0)
      return () => clearTimeout(t)
    }
  }, [editing, rawValue])

  const dirty = draft.trim() !== rawValue.trim()

  async function handleSave() {
    if (!dirty || saving) return
    await onSave(draft.trim())
    setEditing(false)
  }

  return (
    <div className="group rounded-md border border-border/70 bg-background/80 px-3 py-2.5 shadow-sm">
      <div className="flex items-center justify-between gap-2 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <Icon className="h-3 w-3" aria-hidden /> {label}
        </span>
        {!editing && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100 focus:outline-none text-muted-foreground hover:text-foreground"
            aria-label={`Edit ${label.toLowerCase()}`}
          >
            <Pencil className="h-3 w-3" />
          </button>
        )}
      </div>
      {editing ? (
        <div className="mt-1.5 space-y-1.5">
          <Input
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                handleSave()
              } else if (e.key === 'Escape') {
                e.preventDefault()
                setEditing(false)
              }
            }}
            type={inputType}
            inputMode={inputMode}
            autoComplete={autoComplete}
            placeholder={placeholder}
            maxLength={
              inputType === 'email'
                ? 255
                : inputType === 'tel'
                ? MAX_CUSTOMER_PHONE_LEN
                : 100
            }
            className="h-8 text-sm"
            aria-label={label}
          />
          {hint && <p className="text-[10px] text-muted-foreground">{hint}</p>}
          <div className="flex justify-end gap-1.5">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setEditing(false)}
              className="h-7 gap-1 text-xs"
              disabled={saving}
            >
              <X className="h-3 w-3" />
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={handleSave}
              disabled={!dirty || saving}
              className="h-7 gap-1.5 text-xs"
            >
              {saving ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Save className="h-3 w-3" />
              )}
              Save
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-1 text-sm font-medium text-foreground truncate">
          {value ? (
            href ? (
              <a href={href} className="hover:underline">
                {value}
              </a>
            ) : (
              value
            )
          ) : (
            <span className="text-muted-foreground font-normal">—</span>
          )}
        </div>
      )}
    </div>
  )
}

// EditableName is the header variant — large text, no border, pencil on
// hover. Same edit affordance as EditableContactCell so the interaction
// pattern is consistent across the dialog.
function EditableName({
  value,
  onSave,
  saving,
}: {
  value: string
  onSave: (v: string) => Promise<unknown>
  saving: boolean
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)
  const inputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (editing) {
      setDraft(value)
      const t = setTimeout(() => inputRef.current?.focus(), 0)
      return () => clearTimeout(t)
    }
  }, [editing, value])

  const dirty = draft.trim() !== value.trim()

  async function handleSave() {
    if (!dirty || saving) return
    await onSave(draft.trim())
    setEditing(false)
  }

  if (editing) {
    return (
      <div className="flex items-center gap-2">
        <Input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              handleSave()
            } else if (e.key === 'Escape') {
              e.preventDefault()
              setEditing(false)
            }
          }}
          maxLength={100}
          placeholder="Customer name"
          className="h-8 max-w-xs text-base font-semibold"
          aria-label="Customer name"
        />
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={() => setEditing(false)}
          className="h-7 gap-1 text-xs"
          disabled={saving}
        >
          <X className="h-3 w-3" />
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={handleSave}
          disabled={!dirty || saving}
          className="h-7 gap-1.5 text-xs"
        >
          {saving ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <Save className="h-3 w-3" />
          )}
          Save
        </Button>
      </div>
    )
  }

  return (
    <span
      className="group inline-flex items-center gap-2 max-w-full"
    >
      <span className="truncate">
        {value || (
          <span className="italic text-muted-foreground">unnamed customer</span>
        )}
      </span>
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100 focus:outline-none text-muted-foreground hover:text-foreground"
        aria-label="Edit name"
      >
        <Pencil className="h-3.5 w-3.5" />
      </button>
    </span>
  )
}
